/**
 * ReadingProgressBar — thin top-of-viewport bar that fills as the
 * reader scrolls down the article. Medium/Substack parity.
 *
 * Usage: mount once inside an article page. Uses scroll listener +
 * requestAnimationFrame throttle.
 */
import { useEffect, useState } from "react";

export default function ReadingProgressBar({ targetSelector = "[data-reading-target]" }) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let ticking = false;

    const measure = () => {
      const target = document.querySelector(targetSelector) || document.body;
      const rect = target.getBoundingClientRect();
      const viewportH = window.innerHeight || 800;
      const scrolled = Math.min(Math.max(-rect.top, 0), rect.height);
      const total = Math.max(rect.height - viewportH, 1);
      const raw = total > 0 ? (scrolled / total) * 100 : 0;
      setPct(Math.min(Math.max(raw, 0), 100));
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(measure);
        ticking = true;
      }
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [targetSelector]);

  return (
    <div
      className="fixed top-0 left-0 right-0 h-1 z-50 pointer-events-none bg-transparent"
      data-testid="reading-progress-bar"
      aria-hidden="true"
    >
      <div
        className="h-full bg-gradient-to-r from-teal-500 via-amber-500 to-rose-500 transition-[width] duration-100 ease-out"
        style={{ width: `${pct}%` }}
        data-testid="reading-progress-fill"
        data-progress={Math.round(pct)}
      />
    </div>
  );
}
